import { useState } from "react"
import { useFilters } from "~/hooks/useFilters"
import { useRemoveTagOnSkus } from "~/hooks/useTags"
import { trackEvent } from "~/hooks/useTrackingService"
import { useUser } from "~/providers/userProvider"
import { isTagFiltered, toggleTagInFilters } from "~/utils/filter"
import type { Tag } from "~/components/reusable/TagBadgeWithRemove"

type UseTagActionsProps = {
  skuIds: string[]
  onRemoveSuccess?: () => void
}

// Minimal shape the Tags feature needs from a variant row — deliberately narrower than
// IVariantSummary so non-planning tables (e.g. NPI's Product Management grid) can reuse
// these components without carrying the full planning variant type.
export type TaggableVariant = {
  variantId: string
  skuIds: string[]
  tags: Tag[]
}

// A cell can aggregate many variants (e.g. a grouped row), but only some of them carry a
// given tag. When removing a tag, scope the operation to the variants that actually have
// it — otherwise the confirmation count and mutation cover unrelated SKUs.
export const getEntriesWithTag = (
  entries: TaggableVariant[],
  tag: Tag | undefined,
): TaggableVariant[] =>
  tag ? entries.filter((entry) => entry.tags.some((t) => t.id === tag.id)) : []

export function useTagActions({ skuIds, onRemoveSuccess }: UseTagActionsProps) {
  const { permissions } = useUser()
  const { mutate: removeTag } = useRemoveTagOnSkus()
  const { filters, setFilters } = useFilters()
  const [selectedTag, setSelectedTag] = useState<Tag | undefined>(undefined)

  const tagIsFiltered = (tag: Tag) => isTagFiltered(filters, tag.name)

  const filterOnClick = (tag: Tag) => setFilters(toggleTagInFilters(filters, tag.name))

  const onRemoveTag = (tag: Tag, removeFromSkuIds?: string[], variantCount?: number) => {
    removeTag(
      { tagId: tag.id, skuIds: removeFromSkuIds ?? skuIds, variantCount },
      {
        onSuccess: () => {
          trackEvent("Tag Removed")
          setSelectedTag(undefined)
          onRemoveSuccess?.()
        },
      },
    )
  }

  return {
    tagIsFiltered,
    filterOnClick,
    onRemoveTag,
    selectedTag,
    setSelectedTag,
    canEdit: permissions.canEdit,
  }
}
